import type { PainelNavKey } from '@/components/painel/painel-nav';
import type { SiteStaffRole } from '@/lib/cms-types';
import type { XatOfficialRoleLabel } from '@/lib/xat-official-roles';

/** Cargos do painel — alinhar com API futura. */
export type PainelRole = 'super_admin' | 'admin' | 'editor' | 'reviewer';

export type PainelUserStatus = 'active' | 'inactive';

/**
 * Conta admin absoluta do painel (seed: username `admin`).
 * Não confundir com o cargo Spatie `admin`.
 */
export const ABSOLUTE_ADMIN_USERNAME = 'admin';

export function isAbsoluteAdminUsername(username: string | null | undefined): boolean {
  return (username ?? '').trim().toLowerCase() === ABSOLUTE_ADMIN_USERNAME;
}

export function isAbsoluteAdminUser(
  user: { username?: string | null } | null | undefined
): boolean {
  return isAbsoluteAdminUsername(user?.username);
}

/** Outros Super Admins não gerem a conta absoluta; exclusão nunca é permitida. */
export function canManagePainelUserTarget(
  actorUsername: string | null | undefined,
  target: { username?: string | null } | null | undefined
): boolean {
  if (!target) return false;
  if (!isAbsoluteAdminUser(target)) return true;
  return isAbsoluteAdminUsername(actorUsername);
}

export function canDeletePainelUserTarget(
  _actorUsername: string | null | undefined,
  target: { username?: string | null } | null | undefined
): boolean {
  if (!target) return false;
  return !isAbsoluteAdminUser(target);
}

export type PainelPermission =
  | 'guides.create'
  | 'guides.edit'
  | 'guides.publish'
  | 'guides.delete'
  | 'guides.force_delete'
  | 'guides.review'
  | 'guides.featured'
  | 'powers.create'
  | 'powers.edit'
  | 'powers.publish'
  | 'powers.review'
  | 'powers.delete'
  | 'aces.create'
  | 'aces.edit'
  | 'aces.publish'
  | 'aces.review'
  | 'aces.delete'
  | 'categories.create'
  | 'categories.edit'
  | 'categories.delete'
  | 'pages.create'
  | 'pages.edit'
  | 'pages.delete'
  | 'homepage.edit'
  | 'changelog.view'
  | 'changelog.edit'
  | 'team.edit'
  | 'users.view'
  | 'users.create'
  | 'users.edit'
  | 'users.delete'
  | 'permissions.manage'
  | 'analytics.view'
  | 'settings.manage'
  | 'seo.manage'
  | 'icons.manage';

export type PainelMockUser = {
  id: string;
  username: string;
  password: string;
  name: string;
  email: string;
  role: PainelRole;
  status: PainelUserStatus;
  lastLoginAt: string;
  createdAt: string;
  guidesCreated: number;
  pagesEdited: number;
  powersEdited: number;
  /** URL opcional do avatar público. */
  avatarUrl?: string;
  /** Cargo oficial do xat (badge no artigo). `null` = removido explicitamente. */
  officialXatRole?: XatOfficialRoleLabel | null;
  /** Cargo no xat Ajuda (texto abaixo do nome nos artigos). `null` = nenhum. */
  helpCenterRole?: SiteStaffRole | null;
};

export const PAINEL_ROLE_LABELS: Record<PainelRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  editor: 'Editor',
  reviewer: 'Revisor',
};

export const PAINEL_ROLE_DESCRIPTIONS: Record<PainelRole, string> = {
  super_admin: 'Controle total do painel',
  admin: 'Gerenciamento de conteúdo e equipe',
  editor: 'Produção de conteúdo e rascunhos',
  reviewer: 'Revisão e publicação de conteúdos',
};

export const PAINEL_PERMISSION_LABELS: Record<PainelPermission, string> = {
  'guides.create': 'Criar guias',
  'guides.edit': 'Editar guias',
  'guides.publish': 'Publicar guias',
  'guides.delete': 'Excluir guias',
  'guides.force_delete': 'Excluir guias permanentemente',
  'guides.review': 'Revisar guias',
  'guides.featured': 'Destacar guia como popular',
  'powers.create': 'Criar powers',
  'powers.edit': 'Editar powers',
  'powers.publish': 'Publicar powers',
  'powers.review': 'Revisar powers',
  'powers.delete': 'Excluir powers',
  'aces.create': 'Criar aces',
  'aces.edit': 'Editar aces',
  'aces.publish': 'Publicar aces',
  'aces.review': 'Revisar aces',
  'aces.delete': 'Excluir aces',
  'categories.create': 'Criar categorias',
  'categories.edit': 'Editar categorias',
  'categories.delete': 'Excluir categorias',
  'pages.create': 'Criar páginas',
  'pages.edit': 'Editar páginas',
  'pages.delete': 'Excluir páginas',
  'homepage.edit': 'Editar homepage',
  'changelog.view': 'Ver changelogs',
  'changelog.edit': 'Editar changelogs',
  'team.edit': 'Editar equipe',
  'users.view': 'Ver usuários',
  'users.create': 'Criar usuários',
  'users.edit': 'Editar usuários',
  'users.delete': 'Excluir usuários',
  'permissions.manage': 'Gerenciar permissões',
  'analytics.view': 'Ver analytics',
  'settings.manage': 'Configurações do sistema',
  'seo.manage': 'Gerenciar SEO',
  'icons.manage': 'Gerenciar Icon Library',
};

const ALL_PERMISSIONS = Object.keys(PAINEL_PERMISSION_LABELS) as PainelPermission[];

export const PAINEL_ALL_PERMISSIONS: PainelPermission[] = [...ALL_PERMISSIONS];

export const PAINEL_PERMISSION_GROUPS: {
  id: string;
  label: string;
  permissions: PainelPermission[];
}[] = [
  {
    id: 'guides',
    label: 'Guias',
    permissions: [
      'guides.create',
      'guides.edit',
      'guides.publish',
      'guides.delete',
      'guides.force_delete',
      'guides.review',
      'guides.featured',
    ],
  },
  {
    id: 'powers',
    label: 'Powers',
    permissions: ['powers.create', 'powers.edit', 'powers.publish', 'powers.review', 'powers.delete'],
  },
  {
    id: 'aces',
    label: 'Aces',
    permissions: ['aces.create', 'aces.edit', 'aces.publish', 'aces.review', 'aces.delete'],
  },
  {
    id: 'categories',
    label: 'Categorias',
    permissions: ['categories.create', 'categories.edit', 'categories.delete'],
  },
  {
    id: 'pages',
    label: 'Páginas',
    permissions: ['pages.create', 'pages.edit', 'pages.delete', 'homepage.edit'],
  },
  {
    id: 'changelog',
    label: 'Changelogs',
    permissions: ['changelog.view', 'changelog.edit'],
  },
  {
    id: 'team',
    label: 'Equipe',
    permissions: ['team.edit'],
  },
  {
    id: 'users',
    label: 'Usuários',
    permissions: ['users.view', 'users.create', 'users.edit', 'users.delete'],
  },
  {
    id: 'system',
    label: 'Sistema',
    permissions: ['permissions.manage', 'analytics.view', 'settings.manage', 'seo.manage', 'icons.manage'],
  },
];

const SUPER_ADMIN_PERMISSIONS: PainelPermission[] = [...ALL_PERMISSIONS];

const ADMIN_PERMISSIONS: PainelPermission[] = ALL_PERMISSIONS.filter(
  (p) => p !== 'permissions.manage' && p !== 'settings.manage'
);

const EDITOR_PERMISSIONS: PainelPermission[] = [
  'guides.create',
  'guides.edit',
  'powers.create',
  'powers.edit',
  'aces.create',
  'aces.edit',
  'pages.edit',
];

const REVIEWER_PERMISSIONS: PainelPermission[] = [
  'guides.review',
  'guides.publish',
  'powers.edit',
  'powers.publish',
  'powers.review',
  'aces.edit',
  'aces.publish',
  'aces.review',
];

export const ROLE_PERMISSIONS: Record<PainelRole, PainelPermission[]> = {
  super_admin: SUPER_ADMIN_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
  editor: EDITOR_PERMISSIONS,
  reviewer: REVIEWER_PERMISSIONS,
};

/** Chaves de navegação permitidas por cargo — define o menu lateral. */
export const ROLE_NAV_KEYS: Record<PainelRole, PainelNavKey[]> = {
  super_admin: [
    'dashboard',
    'categorias',
    'guias',
    'revisao',
    'powers',
    'aces',
    'paginas',
    'homepage',
    'equipe',
    'regras',
    'conduta',
    'usuarios',
    'permissoes',
    'analytics_visao',
    'analytics_trafego',
    'analytics_pesquisas',
    'analytics_guias',
    'analytics_powers',
    'analytics_cms',
    'analytics_usuarios',
    'changelog_overview',
    'changelog_versions',
    'changelog_categories',
    'textos_painel',
    'identidade',
    'coming_soon',
    'seo',
    'icon_library',
    'configuracoes',
  ],
  admin: [
    'dashboard',
    'categorias',
    'guias',
    'revisao',
    'powers',
    'aces',
    'paginas',
    'homepage',
    'equipe',
    'regras',
    'conduta',
    'usuarios',
    'analytics_visao',
    'analytics_trafego',
    'analytics_pesquisas',
    'analytics_guias',
    'analytics_powers',
    'analytics_cms',
    'analytics_usuarios',
    'changelog_overview',
    'changelog_versions',
    'changelog_categories',
    'identidade',
    'seo',
    'icon_library',
    'configuracoes',
  ],
  editor: ['dashboard', 'guias', 'powers', 'aces', 'paginas', 'regras', 'conduta', 'configuracoes'],
  reviewer: ['dashboard', 'guias', 'revisao', 'powers', 'aces', 'configuracoes'],
};

export const MOCK_PAINEL_USERS: PainelMockUser[] = [
  {
    id: 'u-admin',
    username: 'admin',
    password: 'admin',
    name: 'Admin',
    email: 'admin@ajuda',
    role: 'super_admin',
    status: 'active',
    lastLoginAt: '2026-07-07T14:30:00.000Z',
    createdAt: '2025-01-10T10:00:00.000Z',
    guidesCreated: 42,
    pagesEdited: 18,
    powersEdited: 31,
  },
  {
    id: 'u-cupim',
    username: 'cupim',
    password: 'admin',
    name: 'Cupim',
    email: 'cupim@ajuda',
    role: 'admin',
    status: 'active',
    officialXatRole: 'Volunteer',
    helpCenterRole: 'mainOwner',
    lastLoginAt: '2026-07-06T09:15:00.000Z',
    createdAt: '2025-03-02T11:00:00.000Z',
    guidesCreated: 28,
    pagesEdited: 12,
    powersEdited: 19,
  },
  {
    id: 'u-guinho',
    username: 'guinho',
    password: 'admin',
    name: 'Guinho',
    email: 'guinho@ajuda',
    role: 'editor',
    status: 'active',
    officialXatRole: 'Wiki Editor',
    helpCenterRole: 'mainOwner',
    lastLoginAt: '2026-07-07T08:45:00.000Z',
    createdAt: '2025-05-18T14:00:00.000Z',
    guidesCreated: 15,
    pagesEdited: 6,
    powersEdited: 8,
  },
  {
    id: 'u-paulo',
    username: 'paulo',
    password: 'admin',
    name: 'Paulo',
    email: 'paulo@ajuda',
    role: 'editor',
    status: 'active',
    officialXatRole: 'Developer',
    helpCenterRole: 'membros',
    lastLoginAt: '2026-07-05T16:20:00.000Z',
    createdAt: '2025-06-01T09:30:00.000Z',
    guidesCreated: 11,
    pagesEdited: 4,
    powersEdited: 5,
  },
  {
    id: 'u-editor',
    username: 'editor',
    password: 'admin',
    name: 'Editor',
    email: 'editor@ajuda',
    role: 'editor',
    status: 'active',
    lastLoginAt: '2026-07-09T18:00:00.000Z',
    createdAt: '2026-07-09T18:00:00.000Z',
    guidesCreated: 0,
    pagesEdited: 0,
    powersEdited: 0,
  },
  {
    id: 'u-thuk',
    username: 'thuk',
    password: 'admin',
    name: 'Thuk',
    email: 'thuk@ajuda',
    role: 'reviewer',
    status: 'active',
    lastLoginAt: '2026-07-07T12:00:00.000Z',
    createdAt: '2025-04-12T08:00:00.000Z',
    guidesCreated: 0,
    pagesEdited: 2,
    powersEdited: 3,
  },
];

export function findMockUserByCredentials(
  username: string,
  password: string
): PainelMockUser | null {
  const user = username.trim().toLowerCase();
  return (
    MOCK_PAINEL_USERS.find((u) => u.username === user && u.password === password) ?? null
  );
}

export function findMockUserByUsername(username: string): PainelMockUser | null {
  const user = username.trim().toLowerCase();
  return MOCK_PAINEL_USERS.find((u) => u.username === user) ?? null;
}

export function hasPermission(role: PainelRole, permission: PainelPermission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Usa permissões da sessão (API) quando disponíveis; união com o mapa estático do cargo. */
export function sessionHasPermission(
  session: { role: PainelRole; permissions?: PainelPermission[] } | null,
  permission: PainelPermission
): boolean {
  if (!session) return false;
  if (session.role === 'super_admin') return true;
  if (hasPermission(session.role, permission)) return true;
  if (session.permissions?.length) {
    return session.permissions.includes(permission);
  }
  return false;
}

export function getNavKeysForRole(role: PainelRole): PainelNavKey[] {
  return ROLE_NAV_KEYS[role];
}

export function canAccessNavKey(role: PainelRole, key: PainelNavKey): boolean {
  return ROLE_NAV_KEYS[role].includes(key);
}

/** Resolve a chave de nav a partir do pathname do painel. */
export function resolveNavKeyFromPathname(pathname: string): PainelNavKey | null {
  const normalized = pathname.replace(/\/$/, '') || '/painel';

  if (normalized === '/painel') return 'dashboard';
  if (normalized === '/painel/categorias') return 'categorias';
  if (normalized.startsWith('/painel/guias')) return 'guias';
  if (normalized === '/painel/revisao') return 'revisao';
  if (normalized.startsWith('/painel/powers')) return 'powers';
  if (normalized.startsWith('/painel/aces')) return 'aces';
  if (normalized === '/painel/paginas/homepage') return 'homepage';
  if (normalized.startsWith('/painel/paginas/regras')) return 'regras';
  if (normalized.startsWith('/painel/paginas/conduta')) return 'conduta';
  if (normalized.startsWith('/painel/paginas')) return 'paginas';
  if (normalized.startsWith('/painel/equipe')) return 'equipe';
  if (normalized.startsWith('/painel/usuarios')) return 'usuarios';
  if (normalized.startsWith('/painel/permissoes')) return 'permissoes';
  if (normalized === '/painel/analytics') return 'analytics_visao';
  if (normalized.startsWith('/painel/analytics/trafego')) return 'analytics_trafego';
  if (normalized.startsWith('/painel/analytics/pesquisas')) return 'analytics_pesquisas';
  if (normalized.startsWith('/painel/analytics/guias')) return 'analytics_guias';
  if (normalized.startsWith('/painel/analytics/powers')) return 'analytics_powers';
  if (normalized.startsWith('/painel/analytics/cms')) return 'analytics_cms';
  if (normalized.startsWith('/painel/analytics/usuarios')) return 'analytics_usuarios';
  if (normalized.startsWith('/painel/analytics')) return 'analytics_visao';
  if (normalized.startsWith('/painel/changelog')) {
    if (normalized.includes('/versions')) return 'changelog_versions';
    if (normalized.includes('/categories')) return 'changelog_categories';
    return 'changelog_overview';
  }
  if (normalized.startsWith('/painel/textos')) return 'textos_painel';
  if (normalized.startsWith('/painel/footer') || normalized.startsWith('/painel/identidade')) {
    return 'identidade';
  }
  if (normalized.startsWith('/painel/coming-soon')) return 'coming_soon';
  if (normalized.startsWith('/painel/seo')) return 'seo';
  if (normalized.startsWith('/painel/icon-library')) return 'icon_library';
  if (normalized.startsWith('/painel/configuracoes')) return 'configuracoes';

  return null;
}

export function canAccessPainelPath(role: PainelRole, pathname: string): boolean {
  const key = resolveNavKeyFromPathname(pathname);
  if (!key) return false;
  return canAccessNavKey(role, key);
}
